<script setup lang="ts">
import { onMounted, computed, defineAsyncComponent } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { THEME_COLORS, themeColorStore } from "@/store/themeColor";
import { storeToRefs } from "pinia";
import rcLogoFullLight from "@/assets/icons/rc-logo-full.svg";
import rcLogoFullDark from "@/assets/icons/rc-logo-full-dark.svg";
import {
  IconSunFill,
  IconSunThin,
  IconArrowLeftLongThin,
} from "@packages/icon-library";
import UiTextButton from "@/ui/UiTextButton.vue";
import { useLanguage } from "@/modules/language/composables/useLanguage";
import { useLanguageStore } from "@/modules/language/store";
import { useStore } from "vuex";

const SwitchLanguageModal = defineAsyncComponent(
  () => import("@/modules/language/components/SwitchLanguageModal.vue")
);

const themeStore = themeColorStore();
const { themeColor } = storeToRefs(themeStore);
const { currentLanguageLabel } = useLanguage();
const languageStore = useLanguageStore();
const vuexStore = useStore();

const route = useRoute();
const { t } = useI18n();

const sysCountry = computed(() => vuexStore.state.system.system_country);

onMounted(() => {
  themeStore.SET_THEME_COLOR_FROM_LOCAL_STORAGE();
});

function toggleTheme() {
  const newTheme =
    themeColor.value === THEME_COLORS.LIGHT
      ? THEME_COLORS.DARK
      : THEME_COLORS.LIGHT;
  themeStore.SET_THEME_COLOR(newTheme);
}

function tokenClearing() {
  if (route.name === "Login") {
    return;
  }
  localStorage.removeItem("access_token");
  localStorage.removeItem("emailCode");
  localStorage.removeItem("email");
}

const logoSrc = computed(() => {
  return themeColor.value === "light" ? rcLogoFullDark : rcLogoFullLight;
});

const isInternalBackRoute = computed(() => {
  return route.meta?.is_internal_back_route ?? true;
});

const backButtonRoute = computed<{ name: string; text: string }>(() => {
  if (route.meta?.back_button_route) {
    return route.meta.back_button_route;
  }

  const specialRoutes = ["Login", "NewPassword", "NewManagerPassword"];
  if (specialRoutes.includes(route.name as string)) {
    return {
      text: t("auth.button.toMainPage"),
      name: "Lending",
    };
  }

  if (route.name === "ResetPassword") {
    return { text: t("auth.button.back"), name: "Login" };
  }

  return { text: t("headerButtons.exit"), name: "Lending" };
});
</script>

<template>
  <section class="auth-layout">
    <div class="auth-layout__sidebar sidebar">
      <img class="sidebar__logo" :src="logoSrc" alt="logo" />

      <RouterView name="navigation" />
    </div>

    <div id="form-container" class="form-container">
      <div class="form-container__header">
        <template v-if="isInternalBackRoute">
          <RouterLink
            class="form-container__back-button"
            :to="{ name: backButtonRoute.name }"
            @click="tokenClearing"
          >
            <IconArrowLeftLongThin />
            <p>{{ backButtonRoute.text }}</p>
          </RouterLink>
        </template>
        <template v-else>
          <a
            class="form-container__back-button"
            :href="backButtonRoute.name"
            @click="tokenClearing"
          >
            <IconArrowLeftLongThin />
            <p>{{ backButtonRoute.text }}</p>
          </a>
        </template>

        <div class="form-container__header_right">
          <UiTextButton
            v-show="sysCountry === 'kg'"
            :label="currentLanguageLabel"
            color="primary"
            font-weight="regular"
            @click="languageStore.changeSwitchLangModalState(true)"
          />
          <Transition name="theme-switch" mode="out-in">
            <component
              :is="themeColor === 'light' ? IconSunFill : IconSunThin"
              :key="themeColor"
              class="theme-icon"
              @click="toggleTheme"
            />
          </Transition>
        </div>
      </div>

      <div class="form-container__page">
        <RouterView />
      </div>

      <SwitchLanguageModal />
    </div>
  </section>
</template>

<style lang="scss" scoped>
.auth-layout {
  display: grid;
  grid-template-columns: 320px 1fr;
  height: 100%;
}

.sidebar {
  background: var(--color-semantic-background-normal-bg-secondary);
  padding: 0 40px;

  &__logo {
    width: 112px;
    height: 40px;
    margin: 16px 0;
  }
}

.form-container {
  height: 100%;
  display: grid;
  grid-template-rows: 72px 1fr 48px;
  overflow: auto;

  &__header {
    height: fit-content;
    padding: 40px 40px 0 40px;
    display: flex;
    justify-content: space-between;

    &_right {
      display: flex;
      column-gap: 24px;
    }

    @media (max-width: 767px) {
      padding: 36px 24px;
    }

    @media (max-width: 411px) {
      padding: 36px 16px;
    }
  }

  &__back-button {
    color: var(--color-semantic-content-normal-link);
    padding: 0;
    display: flex;
    gap: 4px;
    text-decoration: none;
    cursor: pointer;

    p {
      cursor: pointer;
    }

    .icon {
      width: 24px;
      height: 24px;
      background: var(--color-semantic-content-normal-link);
    }
  }

  &__page {
    @media (max-width: 1365px) {
      display: flex;
      align-items: center;
    }
    @media (max-width: 767px) {
      display: block;
    }
  }

  &__footer {
    padding: 0 40px;
    display: flex;
    align-items: flex-start;
  }
}

@media (max-width: 1279px) {
  .auth-layout {
    grid-template-columns: 1fr;
  }

  .sidebar {
    display: none;
  }
}

.theme-icon {
  cursor: pointer;
  width: 24px;
  height: 24px;
  background-color: var(--color-semantic-content-normal-primary);
  transition: background-color 0.3s ease-in-out;
  &:hover {
    background-color: var(--color-semantic-content-hover-primary);
  }
}

.theme-switch-enter-active,
.theme-switch-leave-active {
  transition:
    opacity 0.3s,
    transform 0.3s;
  display: inline-block;
}
.theme-switch-enter,
.theme-switch-leave-to {
  opacity: 0;
  transform: rotate(0deg);
}
.theme-switch-enter-to,
.theme-switch-leave {
  opacity: 1;
  transform: rotate(360deg);
}
</style>
